// ChatModelAdapter that bridges @assistant-ui/react to the OpenClaw Gateway.
//
// The gateway is a WebSocket protocol: chat.send kicks off a run, assistant
// text streams as `stream` events, intermediate tool/lifecycle activity comes
// through `event` events, and the canonical message list lives behind
// chat.history. Each adapter.run() invocation:
//
//   1. extracts the latest user message text and forwards it via
//      client.sendMessage(text, { sessionKey, runId })
//   2. listens on the singleton GatewayClient for 'stream' events, yielding
//      AUI snapshots {content: [{type:'text', text: cumulative}]} as deltas
//      land
//   3. on stream.done re-fetches chat.history to canonicalise the last
//      assistant turn (thinking / toolCall / toolResult blocks → AUI parts)
//      and yields one final snapshot containing the typed content list
//   4. forwards AbortSignal cancellation through client.chatAbort()
//
// The adapter is stateless across runs; the gateway client owns the session.

import { getGatewayClient } from './GatewayClient.js';

const DEFAULT_SESSION_KEY = 'agent:main:main';
const HISTORY_LIMIT = 50;

function extractUserText(messages) {
  if (!Array.isArray(messages) || !messages.length) return '';
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role !== 'user') continue;
    if (typeof m.content === 'string') return m.content;
    if (Array.isArray(m.content)) {
      let acc = '';
      for (const c of m.content) {
        if (c?.type === 'text' && typeof c.text === 'string') acc += c.text;
      }
      return acc;
    }
    return '';
  }
  return '';
}

// Convert a gateway message-content block list (text / thinking / toolCall /
// toolResult) into AUI content parts. Tool calls get paired with their
// matching toolResult by callId so AUI renders one tool-call part with a
// populated `result` prop.
// Exported so the history-hydration path in GatewayThread can reuse the
// same shape — historical tool calls then render through GwToolCallPart
// instead of being flattened into a text dump.
export function mapGatewayContentToAui(content) {
  if (!Array.isArray(content)) return [];
  const resultsByCallId = new Map();
  for (const c of content) {
    if (!c || typeof c !== 'object') continue;
    if (c.type === 'toolResult' || c.type === 'tool_result') {
      const id = c.tool_use_id || c.callId || c.id || null;
      if (id) resultsByCallId.set(id, c);
    }
  }
  const parts = [];
  for (const c of content) {
    if (!c || typeof c !== 'object') continue;
    if (c.type === 'text') {
      const text = typeof c.text === 'string' ? c.text : '';
      if (text) parts.push({ type: 'text', text });
      continue;
    }
    if (c.type === 'thinking') {
      const text = typeof c.thinking === 'string' ? c.thinking : '';
      if (text.trim()) parts.push({ type: 'reasoning', text });
      continue;
    }
    if (c.type === 'toolCall' || c.type === 'tool_call') {
      const id = c.id || c.callId || `tc-${parts.length}`;
      const matched = resultsByCallId.get(id) || null;
      const block = {
        type: 'tool-call',
        toolCallId: String(id),
        toolName: c.name || 'tool',
        args: c.arguments ?? {},
      };
      if (matched) {
        const raw = matched.content ?? matched.output ?? matched.result ?? null;
        block.result = raw;
        block.isError = false;
      }
      parts.push(block);
      continue;
    }
    // toolResult is consumed via pairing above; bare/unmatched results are
    // dropped (the gateway should always emit them paired).
  }
  return parts;
}

// Tiny async queue used to fan WebSocket EventTarget callbacks into an
// async iterator the AUI runtime can pull from.
function createQueue() {
  const items = [];
  let pending = null;
  let closed = false;
  return {
    push(item) {
      if (closed) return;
      if (pending) { const r = pending; pending = null; r(item); }
      else items.push(item);
    },
    next() {
      if (items.length) return Promise.resolve(items.shift());
      if (closed) return Promise.resolve(null);
      return new Promise((resolve) => { pending = resolve; });
    },
    close() {
      closed = true;
      if (pending) { const r = pending; pending = null; r(null); }
    },
  };
}

function newRunId() {
  return `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// The gateway-server wraps user chat.send messages with a
// "System: [time] Exec completed (id, code 0) :: <exec output>"
// preamble (sometimes followed by a blank line and a "[time] <real>"
// marker) so the model gets prior exec context. That whole prefix is
// noise in the UI — operators only typed the trailing fragment.
//
// Strategy:
//   1. Peel off any leading System: [<ts>] paragraph by slicing past
//      the next blank line (or to end-of-string if the System block is
//      the only content). Repeat in case multiple System blocks chain.
//   2. Peel a leading "[<ts>] " marker off the survivor so the bubble
//      reads just what the operator typed.
export function stripGatewaySystemPreamble(raw) {
  if (typeof raw !== 'string') return raw;
  let text = raw;
  while (/^System:\s*\[[^\]]+\]/.test(text)) {
    const blankIdx = text.indexOf('\n\n');
    if (blankIdx === -1) {
      // No blank-line separator — try to find the last standalone
      // timestamp marker (e.g. "\n[Sun 2026-06-21 11:40 UTC] ?") and
      // slice from there. Otherwise the whole message is preamble; drop it.
      const matches = [...text.matchAll(/\n\[[^\]]+\][^\n]*/g)];
      const last = matches[matches.length - 1];
      text = last ? text.slice(last.index + 1) : '';
      break;
    }
    text = text.slice(blankIdx + 2).trimStart();
  }
  text = text.replace(/^\[[^\]]+\]\s*/, '');
  return text.trim();
}

// Extract a tool-call snapshot from an incoming WS event frame. Returns
// null for events that aren't about a tool call (lifecycle, approvals,
// etc.). The gateway uses several shapes for tool activity; we accept
// both `agent stream=tool` and `session.tool` here.
function extractToolFromFrame(frame) {
  if (!frame || typeof frame !== 'object') return null;
  const ev = frame.event;
  const p = frame.payload || {};
  if (ev === 'agent' && p.stream === 'tool') {
    const d = p.data || {};
    // Don't default the name here — empty tool frames (verbose-stripped
    // update events, etc.) should fall through as null so applyToolEvent
    // refuses to create phantom 'tool · preparing…' cards.
    const name = d.name || d.tool || null;
    // Gateway emits `data.toolCallId` (camelCase, no underscore). The other
    // shapes are kept as fallbacks for older runtimes / alt code paths.
    const callId = d.toolCallId || d.id || d.tool_use_id || d.call_id || d.callId || null;
    if (!name && !callId) return null;
    const phase = d.phase || (d.done ? 'done' : 'running');
    const args = d.arguments ?? d.args ?? d.input;
    const hasResult = d.result !== undefined || d.output !== undefined || (phase === 'done' && d.content !== undefined);
    const result = hasResult ? (d.result ?? d.output ?? d.content) : undefined;
    const isError = !!(d.error || d.is_error || d.isError);
    return { callId, name, phase, args, result, isError };
  }
  if (ev === 'session.tool') {
    const t = p.tool || {};
    const name = t.name || p.name || 'tool';
    const callId = t.id || t.tool_use_id || p.id || p.tool_use_id || null;
    const phase = p.status || (p.done ? 'done' : 'running');
    const args = t.arguments ?? t.args ?? p.arguments ?? p.args;
    const hasResult = p.result !== undefined || t.result !== undefined;
    const result = hasResult ? (p.result ?? t.result) : undefined;
    const isError = !!(p.error || p.is_error);
    return { callId, name, phase, args, result, isError };
  }
  return null;
}

// In-memory live parts buffer: text + tool-call parts in source order, so
// the bubble interleaves activity as it happens. The cumulative assistant
// text from the gateway is split across multiple text parts at tool
// boundaries — `_start` on each text part records the offset in the
// cumulative stream where that part begins. Stripped before yielding to
// AUI.
function createLiveParts() {
  const state = {
    parts: [],
    toolByCallId: new Map(),
    cumulativeText: '',
  };

  // Reserve the 🤔 "thinking" slot at the TOP of the turn the moment the first
  // body content (text or tool) appears. For openai-completions agents the
  // reasoning block arrives LATE (as a whole session.message), so without a
  // reserved slot it pops in after the body and jumps above it. The placeholder
  // renders as an animated "Thinking…" bubble until the real reasoning fills it
  // in place (applyMessage) — or the run-loop timeout drops it if none comes.
  function ensureReasoningSlot() {
    if (state.parts.some((p) => p.type === 'reasoning')) return false;
    state.parts.unshift({ type: 'reasoning', text: '', pending: true });
    // Everything already indexed shifts down by one.
    state.toolByCallId.forEach((idx, callId) => state.toolByCallId.set(callId, idx + 1));
    return true;
  }

  function applyTextDelta(newText) {
    if (typeof newText !== 'string') return false;
    ensureReasoningSlot();
    const last = state.parts[state.parts.length - 1];
    let idx;
    if (last && last.type === 'text') {
      idx = state.parts.length - 1;
    } else {
      // Last part is a tool-call (or buffer is empty). Open a new text
      // part starting at the cumulative-text boundary that existed when
      // the last tool-call was pushed.
      idx = state.parts.length;
      state.parts.push({ type: 'text', text: '', _start: state.cumulativeText.length });
    }
    state.cumulativeText = newText;
    const start = state.parts[idx]._start || 0;
    const slice = newText.slice(start);
    if (slice === state.parts[idx].text) return false;
    state.parts[idx] = { ...state.parts[idx], text: slice };
    return true;
  }

  function applyToolEvent(t) {
    if (!t) return false;
    if (!(t.callId && state.toolByCallId.has(t.callId))) ensureReasoningSlot();
    if (t.callId && state.toolByCallId.has(t.callId)) {
      const idx = state.toolByCallId.get(t.callId);
      const prev = state.parts[idx];
      const next = { ...prev };
      if (t.name && (prev.toolName === 'tool' || prev.toolName === '')) next.toolName = t.name;
      if (t.args !== undefined) next.args = t.args;
      if (t.result !== undefined) { next.result = t.result; next.isError = !!t.isError; }
      // Compare cheaply — skip yield if nothing changed.
      if (
        next.toolName === prev.toolName
        && next.args === prev.args
        && next.result === prev.result
        && next.isError === prev.isError
      ) return false;
      state.parts[idx] = next;
      return true;
    }
    // Defensive guard against phantom cards. Some gateway versions
    // emit half-shaped tool frames (e.g. agent stream=tool phase=update
    // after the verbose strip removed both name and args, or a stray
    // toolResult message that arrives before its parent assistant
    // toolCall). Those would otherwise spawn synthetic 'tool ·
    // preparing…' cards between the real ones, with unique callIds so
    // the later real event can't upsert them. Require BOTH a real
    // callId AND a usable name to open a new card — anything less can
    // still update an existing card via the upsert branch above, but
    // never spawn a fresh phantom.
    if (!t.callId || !t.name) return false;
    const cid = t.callId;
    const part = {
      type: 'tool-call',
      toolCallId: cid,
      toolName: t.name || 'tool',
      args: t.args ?? {},
    };
    if (t.result !== undefined) { part.result = t.result; part.isError = !!t.isError; }
    state.parts.push(part);
    state.toolByCallId.set(cid, state.parts.length - 1);
    return true;
  }

  // Ingest a full gateway message (the `payload.message` from a
  // session.message frame). Assistant messages with toolCall content get
  // pushed as live tool-call parts (with their args populated, which the
  // stream=tool broadcast strips for openai-completions). toolResult
  // messages pair against the existing call by toolCallId and populate
  // the result. We skip text content here — assistant text is already
  // handled by the cumulative agent stream=assistant deltas; replaying
  // it from session.message would double the visible body.
  function applyMessage(message) {
    if (!message || typeof message !== 'object') return false;
    let changed = false;
    if (message.role === 'assistant' && Array.isArray(message.content)) {
      for (const c of message.content) {
        if (!c || typeof c !== 'object') continue;
        if (c.type === 'toolCall' || c.type === 'tool_call') {
          const callId = c.id || c.callId || c.toolCallId || null;
          // Pass name through raw — no 'tool' default. applyToolEvent's
          // guard rejects new-card creation when name is missing, so
          // half-shaped blocks don't spawn phantom cards.
          if (applyToolEvent({
            callId,
            name: c.name || c.toolName,
            args: c.arguments ?? c.args ?? {},
            phase: 'start',
          })) changed = true;
        } else if (c.type === 'thinking' || c.type === 'reasoning') {
          // The model's chain-of-thought arrives as a complete block in
          // session.message (not streamed) for openai-completions
          // agents. Insert it BEFORE any trailing text parts — by the
          // time session.message lands, agent stream=assistant deltas
          // have already built up the visible text part(s). Pushing
          // reasoning to the end would render it AFTER the body
          // (reversed source order). Walking back from the end and
          // splicing in before the first non-text part keeps the
          // bubble's reading order as 🤔 thinking → tool calls → text.
          const text = (typeof c.thinking === 'string' && c.thinking)
            || (typeof c.text === 'string' && c.text)
            || '';
          if (text.trim()) {
            // Belt-and-braces dedup: scan the WHOLE parts list for an
            // existing reasoning block with the same text. The earlier
            // "check only the neighbor" guard missed the duplicate when
            // another part (a tool-call or text part) sat between the
            // splice point and a prior identical reasoning — which
            // happens whenever a session.message is replayed by the
            // debounced chat.history poll.
            const alreadyPresent = state.parts.some(
              (p) => p.type === 'reasoning' && p.text === text,
            );
            if (!alreadyPresent) {
              // Prefer filling the reserved pending placeholder in place — no
              // reshuffle, no jump; the thought just resolves where it already
              // sits at the top of the turn.
              const slot = state.parts.findIndex((p) => p.type === 'reasoning' && p.pending);
              if (slot !== -1) {
                state.parts[slot] = { type: 'reasoning', text };
                changed = true;
              } else {
                let insertAt = state.parts.length;
                for (let i = state.parts.length - 1; i >= 0; i--) {
                  if (state.parts[i].type === 'text') insertAt = i;
                  else break;
                }
                state.parts.splice(insertAt, 0, { type: 'reasoning', text });
                // Refresh toolByCallId index — splicing shifts every
                // tool-call part that lived at index >= insertAt by one,
                // and we rely on the index map to upsert in place when
                // the result phase arrives.
                state.toolByCallId.forEach((idx, callId) => {
                  if (idx >= insertAt) state.toolByCallId.set(callId, idx + 1);
                });
                changed = true;
              }
            }
          }
        }
      }
      return changed;
    }
    if (message.role === 'toolResult' || message.role === 'tool_result') {
      const callId = message.toolCallId || message.tool_use_id || message.toolUseId || null;
      if (!callId) return false;
      // toolResult.content is usually [{type:'text', text:'…'}]. Flatten
      // into a single string so GwToolCallPart can render it without
      // recursing into typed parts. JSON-ish results stay as strings —
      // GwToolCallPart parses them via tryParseJson and routes to
      // JsonValueCard / MiniTable when appropriate.
      let result;
      if (Array.isArray(message.content)) {
        result = message.content
          .filter((c) => c && (c.type === 'text' || typeof c.text === 'string'))
          .map((c) => (typeof c.text === 'string' ? c.text : ''))
          .join('');
      } else if (typeof message.content === 'string') {
        result = message.content;
      } else if (message.content && typeof message.content === 'object') {
        try { result = JSON.stringify(message.content); } catch { result = String(message.content); }
      }
      return applyToolEvent({
        callId,
        name: message.toolName,
        result,
        isError: !!message.isError,
        phase: 'result',
      });
    }
    return false;
  }

  // True while the reserved thinking slot is still empty (no reasoning yet).
  function hasPendingReasoning() {
    return state.parts.some((p) => p.type === 'reasoning' && p.pending);
  }

  // Remove the placeholder if the turn produced no reasoning (some agents
  // never emit a chain-of-thought). Called by the run-loop timeout.
  function dropPendingReasoning() {
    const slot = state.parts.findIndex((p) => p.type === 'reasoning' && p.pending);
    if (slot === -1) return false;
    state.parts.splice(slot, 1);
    state.toolByCallId.forEach((idx, callId) => {
      if (idx > slot) state.toolByCallId.set(callId, idx - 1);
    });
    return true;
  }

  function snapshot() {
    return state.parts.map((p) => {
      if (p.type === 'text') return { type: 'text', text: p.text };
      if (p.type === 'reasoning') return { type: 'reasoning', text: p.text };
      // tool-call parts are already AUI-shaped; clone to avoid mutating
      // the buffer if AUI freezes content.
      return { ...p };
    });
  }

  return { applyTextDelta, applyToolEvent, applyMessage, snapshot, hasPendingReasoning, dropPendingReasoning };
}

export function createGatewayAdapter({ getSessionKey, client: boundClient = null, takeAttachmentsMarkdown = null } = {}) {
  // boundClient: a per-window GatewayClient instance — each chat window owns
  // its own WebSocket connection. Falls back to the shared singleton for
  // callers that don't manage a dedicated client.
  const resolveClient = () => boundClient || getGatewayClient();
  const resolveSession = () => {
    try {
      const k = getSessionKey?.();
      if (k) return String(k);
    } catch {}
    const client = resolveClient();
    return client.sessionKey || DEFAULT_SESSION_KEY;
  };

  return {
    async *run({ messages, abortSignal }) {
      let text = extractUserText(messages);
      // Drain any images the composer attached as pills: they ride the
      // outgoing message as markdown so the agent gets fetchable URLs.
      const attachmentsMd = typeof takeAttachmentsMarkdown === 'function'
        ? String(takeAttachmentsMarkdown() || '')
        : '';
      if (attachmentsMd) text = text.trim() ? `${text.trim()}\n\n${attachmentsMd}` : attachmentsMd;
      if (!text.trim()) return;

      const client = resolveClient();
      const sessionKey = resolveSession();
      const runId = newRunId();

      const queue = createQueue();
      const onStream = (e) => {
        const d = e.detail || {};
        // Drop streaming text that belongs to a KNOWN OTHER session. This is
        // what stops a backend job (e.g. ICP fit runs under
        // agent:main:backend:icp_fit:*) or another chat window from bleeding
        // into this one over the shared socket. Frames whose session can't be
        // resolved still pass, so this can never starve the active window.
        if (d.sessionKey && d.sessionKey !== sessionKey) return;
        queue.push({ type: 'stream', text: d.text || '', done: !!d.done });
      };
      const onEvent = (e) => {
        const frame = e.detail;
        if (frame && frame.__sk && frame.__sk !== sessionKey) return; // known-foreign session → ignore
        queue.push({ type: 'event', frame });
      };
      client.addEventListener('stream', onStream);
      client.addEventListener('event', onEvent);

      let aborted = false;
      const onAbort = () => {
        aborted = true;
        client.chatAbort({ sessionKey, runId }).catch(() => {});
        queue.close();
      };
      if (abortSignal) {
        if (abortSignal.aborted) onAbort();
        else abortSignal.addEventListener('abort', onAbort, { once: true });
      }

      const live = createLiveParts();

      // If the reserved 🤔 "thinking" slot isn't filled within this window of
      // the first body content, drop it — the turn produced no reasoning.
      const REASONING_WAIT_MS = 700;
      let reasoningTimer = null;
      const armReasoningTimeout = () => {
        if (reasoningTimer || !live.hasPendingReasoning()) return;
        reasoningTimer = setTimeout(() => { queue.push({ type: 'reasoning-timeout' }); }, REASONING_WAIT_MS);
      };

      // Track which gateway messageIds we've already pulled into the
      // live parts buffer so periodic chat.history refreshes don't
      // re-apply old turns. The current-turn boundary is the last
      // user-role message in history at run start (we don't want to
      // replay assistant turns from before this run).
      const appliedMessageIds = new Set();
      // Immutable snapshot of the ids that existed BEFORE this run's
      // message was sent. appliedMessageIds keeps growing during the run;
      // baselineIds is what lets the canonicalisation pass tell "prior
      // turn" from "this turn" when the session was busy and chat.history
      // lags behind the live run.
      const baselineIds = new Set();
      let historyBaselineSet = false;
      let pollScheduled = false;
      let lastPollAt = 0;
      let historyFetchInFlight = false;
      // Set when the gateway finalised our run with NO output — the agent
      // was busy with an earlier request and our message got queued. In
      // that state we keep the run open and watch history for the real
      // reply instead of ending the turn empty.
      let waitingForReply = false;
      // Persistent (not per-poll): a fresh assistant message that ended its
      // turn (stopReason 'stop') has been seen. If it lands BEFORE the
      // empty run-final flips waitingForReply, the flag lets that branch
      // finish the turn immediately instead of waiting forever.
      let freshFinalAssistantSeen = false;

      const refetchAndApplyHistory = async () => {
        if (historyFetchInFlight) return false;
        historyFetchInFlight = true;
        try {
          const payload = await client.chatHistory({ sessionKey, limit: HISTORY_LIMIT });
          const list = Array.isArray(payload?.messages) ? payload.messages : [];
          // First call: this runs BEFORE client.sendMessage, so by
          // definition every message currently in chat.history belongs
          // to a prior turn — the new user prompt isn't on the server
          // yet. Seed appliedMessageIds with all of them and apply
          // NONE. The new turn's content arrives via session.message
          // frames (and subsequent poll deltas) carrying brand-new
          // message ids the live buffer hasn't seen.
          //
          // (The previous implementation walked back to the "last user
          // role" and replayed everything after it — which on turn N
          // means it replayed turn N-1's whole assistant response into
          // the new bubble, stacking stale tool cards + reasoning into
          // the live view.)
          if (!historyBaselineSet) {
            historyBaselineSet = true;
            for (const m of list) {
              const id = m?.__openclaw?.id || m?.id;
              if (id) { appliedMessageIds.add(id); baselineIds.add(id); }
            }
            return;
          }
          let changed = false;
          for (const m of list) {
            const id = m?.__openclaw?.id || m?.id;
            if (!id || appliedMessageIds.has(id)) continue;
            appliedMessageIds.add(id);
            if (live.applyMessage(m)) changed = true;
            // applyMessage skips assistant TEXT (normally it streams in via
            // agent deltas). While we're waiting behind a busy run those
            // deltas may never come, so a fresh assistant message that
            // ended its turn (stopReason 'stop') is our cue to finish the
            // run — the canonicalisation pass then rebuilds the whole turn
            // from history, text included.
            if (
              m.role === 'assistant'
              && (m.stopReason === 'stop' || m.stopReason === 'end_turn')
            ) freshFinalAssistantSeen = true;
          }
          if (waitingForReply && freshFinalAssistantSeen) queue.push({ type: 'turn-final' });
          return changed;
        } catch {
          return false;
        } finally {
          historyFetchInFlight = false;
        }
      };

      const schedulePoll = () => {
        if (pollScheduled) return;
        const now = Date.now();
        const delay = Math.max(150, 500 - (now - lastPollAt));
        pollScheduled = true;
        setTimeout(() => {
          pollScheduled = false;
          lastPollAt = Date.now();
          refetchAndApplyHistory().then((changed) => {
            if (changed) queue.push({ type: 'history-update' });
          });
        }, delay);
      };

      // Slow safety-net poller for the queued-behind-a-busy-run state.
      // In that state the gateway can go quiet (no session.message, no
      // sessions.changed — history frozen mid-run), so the event-driven
      // schedulePoll() alone would never fire and the reply would be
      // missed. Declared outside the try so the finally can clear it.
      let waitPoller = null;
      const startWaitPoller = () => {
        if (waitPoller) return;
        waitPoller = setInterval(() => schedulePoll(), 4000);
      };

      try {
        // Seed the baseline of already-processed messageIds BEFORE the
        // new turn starts streaming results back — without this, the
        // first poll would re-apply every prior assistant turn as if
        // it were fresh content.
        await refetchAndApplyHistory();

        // Fire the request. Any error surfaces back to AUI as a thrown.
        try {
          await client.sendMessage(text, { runId, sessionKey });
        } catch (err) {
          const hint = err?.error?.details?.recommendedNextStep || err?.error?.code || '';
          yield {
            content: [{
              type: 'text',
              text: `**error:** ${err.message || err}${hint ? ` _(${hint})_` : ''}`,
            }],
          };
          return;
        }

        let done = false;
        // True once this run has produced ANY visible content (text, tool
        // card, or reasoning). Gates the busy-session detection below.
        const hasTurnContent = () => live.snapshot().some((p) => (
          (p.type === 'text' && p.text && p.text.trim())
          || p.type === 'tool-call'
          || (p.type === 'reasoning' && p.text && p.text.trim())
        ));
        while (!done && !aborted) {
          const item = await queue.next();
          if (!item) break;
          let changed = false;
          let transient = null;
          if (item.type === 'stream') {
            changed = live.applyTextDelta(item.text || '');
            if (item.done) {
              if (changed || hasTurnContent()) {
                // Normal completion — the run streamed real output.
                done = true;
              } else if (!waitingForReply) {
                // The gateway finalised our run with NO output: the agent
                // is busy with an earlier request and our message was
                // queued into the running session. Ending the turn here is
                // what used to dump the previous run's stale tool calls
                // into this bubble (the canonicalisation pass walked back
                // to a PRIOR user boundary). Instead: stay open, tell the
                // user, and watch history until the real reply lands.
                waitingForReply = true;
                if (freshFinalAssistantSeen) {
                  // The reply already landed via an earlier history poll
                  // (text-only, so the live buffer skipped it) — finish
                  // now; canonicalisation renders it from history.
                  done = true;
                } else {
                  transient = [{
                    type: 'text',
                    text: '⏳ _The agent is still finishing a previous request — your message is queued and the reply will appear here._',
                  }];
                  startWaitPoller();
                }
              }
            }
          } else if (item.type === 'event') {
            const frame = item.frame;
            // Path 1 — agent stream=tool / session.tool frames. These are
            // the cap-gated, verbose-filtered ones. They arrive when they
            // arrive; we still try them in case the runtime started
            // honouring caps for our connId.
            const tool = extractToolFromFrame(frame);
            if (tool) changed = live.applyToolEvent(tool) || changed;
            // Path 2 — session.message frames. This is the path that
            // actually carries usable args + results for openai-completions
            // agents (zai/glm-5 etc.). Each assistant turn's toolCall and
            // each toolResult arrives as a separate session.message during
            // the run; the live parts buffer interleaves them in source
            // order.
            if (frame && frame.event === 'session.message') {
              const msg = frame.payload?.message;
              if (msg) {
                // Tag this message as already-applied BEFORE handing it
                // to the live buffer. The schedulePoll() below fires a
                // chat.history fetch that walks the same message back —
                // without this guard we'd re-apply it and duplicate the
                // reasoning block (tool-call upserts are idempotent, but
                // reasoning splices aren't).
                const id = msg.__openclaw?.id || msg.id;
                if (id) appliedMessageIds.add(id);
                if (live.applyMessage(msg)) changed = true;
              }
            }
            // Path 3 — when sessions.changed signals a new message was
            // committed to this session, poll chat.history (debounced) so
            // the live tool cards transition to 'done' as their individual
            // toolResults land — even if session.message itself was
            // batched / dropped / not broadcast for this message kind.
            if (
              frame
              && (frame.event === 'sessions.changed' || frame.event === 'session.message')
              && frame.payload?.sessionKey === sessionKey
            ) {
              schedulePoll();
            }
          } else if (item.type === 'history-update') {
            // The debounced poll surfaced new messages — yield the updated
            // snapshot. No further work needed here.
            changed = true;
          } else if (item.type === 'turn-final') {
            // While queued behind a busy run, the history poll saw a fresh
            // assistant message that ended its turn — the reply is in.
            // Finish; the canonicalisation pass below renders it.
            done = true;
            changed = true;
          } else if (item.type === 'reasoning-timeout') {
            // No reasoning arrived in time — retire the placeholder so the
            // body isn't left under an empty "Thinking…" bubble.
            if (live.hasPendingReasoning()) changed = live.dropPendingReasoning();
          }
          if (transient) {
            yield { content: transient };
          } else if (changed || done) {
            yield { content: live.snapshot() };
          }
          // Once body content has reserved the thinking slot, start the
          // fallback timer (idempotent — only arms once, only while pending).
          armReasoningTimeout();
        }

        if (!aborted) {
          // Canonicalise from history so the final assistant turn carries
          // typed reasoning + tool-call parts (not just text). A single
          // assistant "turn" can span multiple history messages — when
          // the model calls a tool, the gateway emits:
          //   N:   assistant   [{toolCall ...}]
          //   N+1: toolResult  [{type:'text', text: '...'}]
          //   N+2: assistant   [{text: 'Updated! ✅ ...'}]
          // …all from one user prompt. The PRIOR fix walked backward,
          // grabbed the LAST assistant message (just the text), and
          // replaced our live tool cards with text — blowing the cards
          // away. Walk back to the most recent `user` boundary instead
          // and merge every message after it (assistant + toolResult)
          // into a single content array, in chronological order.
          try {
            const payload = await client.chatHistory({ sessionKey, limit: HISTORY_LIMIT });
            const list = Array.isArray(payload?.messages) ? payload.messages : [];
            // Walk back to the last `user` message — but ONLY treat it as
            // this turn's boundary if it arrived during this run (its id is
            // not in the run-start baseline). When the session was busy
            // with a background run, chat.history lags: the window may not
            // even contain our own message yet, and its last user message
            // is a PRIOR turn's prompt. Slicing after that boundary used to
            // replay the previous run's entire tool-call tail into this
            // bubble. If no fresh boundary exists, skip canonicalisation
            // and keep the live snapshot.
            let startIdx = -1;
            for (let i = list.length - 1; i >= 0; i--) {
              const m = list[i];
              if (m?.role !== 'user') continue;
              const id = m?.__openclaw?.id || m?.id;
              // Older gateways may omit message ids entirely — then the
              // baseline is empty and freshness can't be checked; fall back
              // to trusting the boundary (pre-fix behaviour).
              const fresh = id ? !baselineIds.has(id) : baselineIds.size === 0;
              if (fresh) startIdx = i + 1;
              break;
            }
            if (startIdx === -1) throw new Error('no fresh user boundary — keep live snapshot');
            const turn = list.slice(startIdx).filter((m) => {
              if (!m || m.role === 'user') return false;
              const id = m?.__openclaw?.id || m?.id;
              return !(id && baselineIds.has(id));
            });
            const merged = [];
            for (const m of turn) {
              if (m.role === 'assistant') {
                merged.push(...(Array.isArray(m.content) ? m.content : []));
              } else if (m.role === 'toolResult' || m.role === 'tool_result') {
                // Lift the bare toolResult into the same shape mapGateway
                // expects in an assistant message's content array.
                const id = m.tool_use_id || m.toolCallId || m.tool_call_id || null;
                merged.push({
                  type: 'toolResult',
                  tool_use_id: id,
                  content: m.content,
                });
              }
            }
            const parts = mapGatewayContentToAui(merged);
            if (parts.length) yield { content: parts };
          } catch {
            // Keep the live snapshot as the final output.
          }
        }
      } finally {
        if (reasoningTimer) clearTimeout(reasoningTimer);
        if (waitPoller) clearInterval(waitPoller);
        client.removeEventListener('stream', onStream);
        client.removeEventListener('event', onEvent);
        if (abortSignal) abortSignal.removeEventListener('abort', onAbort);
      }
    },
  };
}
